import { useEffect, useRef } from "react";
import * as THREE from "three/webgpu";
import {
  hasAnySelection,
  hasVertexSelection,
} from "../modeling/editSelection.ts";
import {
  getCommandMetadata,
  getScrollParam,
  usesTopologyPreview,
} from "../modeling/operatorParams.ts";
import { previewEdgeLoop } from "../modeling/ops/edgeLoop.ts";
import { computeProportionalWeights } from "../modeling/proportional.ts";
import { createRafCoalescer } from "../rafCoalescer.ts";
import {
  showApplyGeneratorDialog,
  showConfirmDialog,
  showSaveOrDiscardDialog,
} from "../ui/ConfirmDialog.tsx";
import { useFloatingPanelStore } from "../ui/floatingPanelStore.ts";
import { toggleAxisView } from "../viewport/cameraViews.ts";
import { PRECISION_SCALE } from "../viewport/interactionConstants.ts";
import { screenToWorldRadius } from "../viewport/screenRadius.ts";
import { setActivePage } from "./activePage.ts";
import { useAuthStore } from "./authStore.ts";
import {
  importBlendAndLoad,
  importMeshFileAndSpawn,
} from "./importToLibrary.ts";
import {
  CAMERA_KEYS,
  isEditableField,
  type KeyContext,
  lookupAction,
  normalizeKey,
  ZOOM_IN_KEYS,
  ZOOM_OUT_KEYS,
} from "./keymap.ts";
import {
  isMeshImportFileName,
  MESH_IMPORT_ACCEPT,
} from "./meshImportFormats.ts";
import { selectionToLeafObjectIds } from "./objectSelection.ts";
import { clearSessionState } from "./persistence.ts";
import { saveSceneToCloudLibrary } from "./saveToLibrary.ts";
import { useStore } from "./store.ts";
import { commandSnapshotObject } from "./storeUndo.ts";

/**
 * Set of keys currently held for camera movement.
 * Tracked as a ref so the animation loop can read it without re-renders.
 */
export type CameraKeys = Set<string>;

/**
 * Accumulated mouse movement deltas for fly-mode mouse look.
 * Written by the pointermove handler, consumed (and zeroed) each frame
 * by CameraMovement.
 */
export const flyMouseDelta = { x: 0, y: 0 };

/**
 * Global keyboard shortcut dispatcher.
 *
 * All shortcut-to-action mappings live in keymap.ts.  This hook reads the
 * current app state, determines the active keymap contexts, looks up the
 * action for the pressed key, and dispatches it.
 *
 * Returns a ref to the currently-held camera movement keys (WASD/QE in fly
 * mode, +/-) so the viewport can consume them in its render loop.
 */
export function useKeyboardShortcuts(): React.RefObject<CameraKeys> {
  const cameraKeysRef = useRef<CameraKeys>(new Set());
  // Tracks Shift-hold precision mode for keyboard-initiated transforms (G/R/S + drag).
  // Active while Shift is held during a live command; deactivates on release.
  const kbShiftPrecisionRef = useRef({ active: false, anchorX: 0, anchorY: 0 });

  useEffect(() => {
    // ---------------------------------------------------------------
    // Action dispatcher — maps action IDs to store calls
    // ---------------------------------------------------------------
    function dispatch(action: string) {
      const s = useStore.getState();
      const inEditMode = s.mode === "edit" && s.editMode != null;
      const poseState = s.poseMode;
      const hasBoneSelected = (poseState?.selectedBones?.size ?? 0) > 0;

      switch (action) {
        // ── Command buffer ─────────────────────────────────────
        case "command.cancel":
          s.cancelCommand();
          break;
        case "command.confirm":
          s.confirmCommand();
          break;
        case "command.axis.x":
          s.setCommandAxis("x");
          break;
        case "command.axis.y":
          s.setCommandAxis("y");
          break;
        case "command.axis.z":
          s.setCommandAxis("z");
          break;
        case "command.backspace":
          s.backspaceCommand();
          break;

        // ── Global ─────────────────────────────────────────────
        case "toggleOperatorSearch":
          s.setOperatorSearchOpen(!s.operatorSearchOpen);
          break;
        case "toggleRedoPanel":
          // F9: toggle redo panel visibility — if lastOperation exists, toggle
          // by clearing it; if no lastOperation, this is a no-op.
          if (s.lastOperation) s.clearLastOperation();
          break;
        case "undo":
          s.undo();
          break;
        case "redo":
          s.redo();
          break;
        case "saveScene":
          if (useAuthStore.getState().storageUsage?.tier === "unsubscribed") {
            // Trial: save to local file download instead of cloud library
            s.saveSceneToFile(false);
          } else {
            saveSceneToCloudLibrary();
          }
          break;
        case "saveSceneAs":
          s.saveSceneToFile(true);
          break;
        case "newScene":
          if (s.sceneDirty) {
            showConfirmDialog(
              "You have unsaved changes. Discard and create a new scene?",
            ).then((ok) => {
              if (ok) useStore.getState().newScene();
            });
          } else {
            s.newScene();
          }
          break;
        case "openScene":
          openSceneFilePicker();
          break;
        case "backToLibrary":
          // Trial users have no library — send them to account settings instead
          if (useAuthStore.getState().storageUsage?.tier === "unsubscribed") {
            setActivePage("accountSettings");
            break;
          }
          (async () => {
            const st = useStore.getState();
            if (st.sceneDirty) {
              const result = await showSaveOrDiscardDialog();
              if (result === "cancel") return;
              if (result === "save") {
                useStore.getState().setLoading(true);
                let ok = false;
                try {
                  ok = await saveSceneToCloudLibrary();
                } finally {
                  useStore.getState().setLoading(false);
                }
                if (!ok) return;
              }
              if (result === "discard") {
                clearSessionState();
              }
            }
            setActivePage("library");
          })();
          break;
        case "toggleDock":
          useFloatingPanelStore.getState().toggleDockCollapsed();
          break;
        case "openRenderMethodModal":
          s.setRenderMethodModalOpen(true);
          break;
        case "toggleSettings":
          s.setSettingsOpen(!s.settingsOpen);
          break;
        case "toggleDebug":
          s.setDebugEnabled(!s.debugEnabled);
          break;
        case "openDebugConsole":
          if (s.debugEnabled && s.debugPanelOpen.console) {
            s.toggleDebugPanel("console");
          } else {
            if (!s.debugEnabled) s.setDebugEnabled(true);
            if (!s.debugPanelOpen.console) s.toggleDebugPanel("console");
          }
          break;
        case "selectAll":
          s.selectAll();
          break;
        case "toggleAddObject":
          if (!s.addObjectOpen) {
            s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
            s.setAddObjectFocusSearch(true);
          } else {
            s.setOperatorAnchorPoint(null);
          }
          s.setAddObjectOpen(!s.addObjectOpen);
          break;
        case "deselectAll":
          if (inEditMode) s.clearVertexSelection();
          else s.clearSelection();
          break;
        case "toggleEditPoseMode":
          if (inEditMode) {
            s.exitEditMode();
          } else if (poseState) {
            s.exitPoseMode();
          } else if (s.sculptMode) {
            s.exitSculptMode();
          } else if (s.sketchMode) {
            s.exitSketchMode();
          } else {
            const sel = selectionToLeafObjectIds(
              s.blendFiles,
              s.collections,
              s.selectedObjectTargets,
            );
            if (sel.size === 1) {
              const id = sel.values().next().value!;
              const obj = s.sceneObjects.get(id);
              if (obj) {
                if (obj.objectKind === "mannequin") {
                  s.enterPoseMode(id);
                } else {
                  // Check if the object is covered by a generator.
                  let generatorCollId: string | null = null;
                  for (const [collId, coll] of s.collections) {
                    if (coll.generatorRef && coll.objectIds.has(id)) {
                      generatorCollId = collId;
                      break;
                    }
                  }
                  const isGroup = generatorCollId !== null;
                  if (obj.generator || isGroup) {
                    // Prompt asynchronously; dispatch is synchronous so we
                    // fire-and-forget — the handler reads fresh state after
                    // the await.
                    (async () => {
                      const confirmed = await showApplyGeneratorDialog(isGroup);
                      if (!confirmed) return;
                      const latest = useStore.getState();
                      if (isGroup) {
                        latest.applyGenerator({
                          kind: "collection",
                          id: generatorCollId!,
                        });
                      } else {
                        latest.applyGenerator({ kind: "object", id });
                        latest.enterEditMode(id);
                      }
                    })();
                  } else {
                    s.enterEditMode(id);
                  }
                }
              } else {
                // Not a SceneObject — try as blend object (armature or skinned mesh)
                s.enterPoseMode(id);
              }
            }
          }
          break;
        case "cycleRenderMethod":
          s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
          s.setRenderMethodPieOpen(true);
          break;
        case "openViewPie":
          s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
          s.setViewPieOpen(true);
          break;
        case "openSnapPie":
          if (
            s.workspaceMode === "object" ||
            s.workspaceMode === "edit" ||
            s.workspaceMode === "pose"
          ) {
            s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
            s.setSnapPieOpen(true);
          }
          break;
        case "openExtrudePie":
          if (s.editMode && !s.editMode.isCurve) {
            s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
            s.setExtrudePieOpen(true);
          }
          break;
        case "toggleProportional":
          s.setProportionalEnabled(!s.proportionalEnabled);
          break;
        case "toggleSnap":
          s.setSnapEnabled(!s.snapEnabled);
          break;
        case "cursor.toWorldOrigin":
          s.snapCursorTo("worldOrigin");
          break;
        // ── Edit mode ──────────────────────────────────────────
        case "openDeleteMenu":
          if (s.editMode?.isCurve) {
            s.deleteCurvePoints();
          } else {
            s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
            s.setDeleteMenuOpen(true);
          }
          break;
        case "openMergeMenu":
          if (
            s.editMode &&
            s.editMode.selectedVertices.size >= 2 &&
            s.editMode.granularity === "vertex"
          ) {
            s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
            s.setMergeMenuOpen(true);
          }
          break;
        case "openVertexMenu":
          if (s.editMode && !s.editMode.isCurve) {
            s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
            s.setMeshMenuSection("vertex");
            s.setMeshMenuOpen(true);
          }
          break;
        case "openEdgeMenu":
          if (s.editMode && !s.editMode.isCurve) {
            s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
            s.setMeshMenuSection("edge");
            s.setMeshMenuOpen(true);
          }
          break;
        case "openFaceMenu":
          if (s.editMode && !s.editMode.isCurve) {
            s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
            s.setMeshMenuSection("face");
            s.setMeshMenuOpen(true);
          }
          break;
        case "extrude": {
          const em = s.editMode;
          if (em && hasVertexSelection(em)) {
            if (em.isCurve) {
              s.extendCurve();
            } else {
              s.extrudeSelection();
            }
          }
          break;
        }
        case "extrude_along_normals": {
          const em = s.editMode;
          if (em && hasVertexSelection(em) && !em.isCurve) {
            s.extrudeAlongNormalsSelection();
          }
          break;
        }
        case "inset_faces": {
          const em = s.editMode;
          if (em && hasVertexSelection(em)) s.insetSelection();
          break;
        }
        case "bevel": {
          const em = s.editMode;
          if (em && hasVertexSelection(em)) s.bevelSelection();
          break;
        }
        case "insert_edge_loop": {
          const em = s.editMode;
          if (!em) break;
          s.enterEdgeLoopInsert();
          break;
        }
        case "fill": {
          const em = s.editMode;
          if (!em) break;
          s.fillSelection();
          break;
        }
        case "connect_vertex_path": {
          if (s.editMode) s.connectVertexPath();
          break;
        }
        case "select_connected":
        case "select_connected_add": {
          if (!s.editMode) break;
          if (hasVertexSelection(s.editMode)) {
            s.pushUndo();
            s.selectConnectedFromSelection();
          }
          break;
        }
        case "granularity.vertex":
          if (!s.editMode?.isCurve) s.setEditGranularity("vertex");
          break;
        case "granularity.edge":
          if (!s.editMode?.isCurve) s.setEditGranularity("edge");
          break;
        case "granularity.face":
          if (!s.editMode?.isCurve) s.setEditGranularity("face");
          break;
        case "box_select":
          // Handled directly by BoxSelectHandler's keydown listener
          break;
        case "brush_select":
          // Handled directly by BrushSelectHandler's keydown listener
          break;
        case "selectMore":
          if (s.editMode) s.selectMore();
          break;
        case "selectLess":
          if (s.editMode) s.selectLess();
          break;
        case "rip": {
          const em = s.editMode;
          if (!em) break;
          if (em.isCurve) {
            // V key on curves: cycle handle types instead of rip
            if (hasVertexSelection(em)) {
              s.pushUndo();
              const obj = s.sceneObjects.get(em.objectId);
              if (obj?.curveData) {
                const firstSel = [...em.selectedVertices].find(
                  (vi: number) => vi % 3 === 0,
                );
                if (firstSel != null) {
                  const pi = Math.floor(firstSel / 3);
                  const pt = obj.curveData.points[pi];
                  const order: Array<"auto" | "aligned" | "vector" | "free"> = [
                    "auto",
                    "aligned",
                    "vector",
                    "free",
                  ];
                  const idx = order.indexOf(pt.handleTypeLeft);
                  const next = order[(idx + 1) % order.length];
                  s.setCurveHandleType(next);
                }
              }
            }
          } else if (em.selectedVertices.size >= 1) {
            s.ripSelection(lastPointerX, lastPointerY);
          }
          break;
        }
        case "separate": {
          const em = s.editMode;
          if (em && em.selectedVertices.size >= 1) {
            s.separateSelection();
          }
          break;
        }
        case "shrink_fatten": {
          const em = s.editMode;
          if (em && hasVertexSelection(em)) {
            s.enterCommand("shrinkFatten");
          }
          break;
        }
        case "smooth": {
          const em = s.editMode;
          if (em && hasAnySelection(em) && !em.isCurve) {
            s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
            s.smoothSelection();
          }
          break;
        }
        case "skin_radius": {
          const em = s.editMode;
          if (em && hasVertexSelection(em) && !em.isCurve) {
            s.enterCommand("skinRadius");
          }
          break;
        }
        case "toggle_cyclic":
          if (s.editMode?.isCurve) {
            s.pushUndo();
            s.toggleCurveCyclic();
          }
          break;

        // ── Sculpt mode ───────────────────────────────────────
        case "sculpt.brush.draw":
          s.setSculptBrush("draw");
          break;
        case "sculpt.brush.smooth":
          s.setSculptBrush("smooth");
          break;
        case "sculpt.brush.flatten":
          s.setSculptBrush("flatten");
          break;
        case "sculpt.brush.grab":
          s.setSculptBrush("grab");
          break;
        case "sculpt.invert.toggle":
          if (s.sculptMode) s.setSculptInvert(!s.sculptMode.invert);
          break;
        case "sculpt.radius.down":
          if (s.sculptMode) s.setSculptRadius(s.sculptMode.radius * 0.8);
          break;
        case "sculpt.radius.up":
          if (s.sculptMode) s.setSculptRadius(s.sculptMode.radius * 1.25);
          break;

        // ── Sketch mode ────────────────────────────────────────
        case "sketch.tool.pencil":
          s.setSketchTool("pencil");
          break;
        case "sketch.tool.erase":
          s.setSketchTool("erase");
          break;
        case "sketch.radius.down":
          if (s.sketchMode)
            s.setSketchThickness(Math.max(1, s.sketchMode.thickness * 0.8));
          break;
        case "sketch.radius.up":
          if (s.sketchMode)
            s.setSketchThickness(Math.min(40, s.sketchMode.thickness * 1.25));
          break;

        // ── Hide / unhide (shared action name, mode-aware) ────
        case "hideSelection":
          s.hideSelection();
          break;
        case "hideUnselected":
          s.hideUnselected();
          break;
        case "unhideAll":
          s.unhideAll();
          break;
        case "toggleEditXray":
          s.toggleEditXray();
          break;
        case "toggleObjectXray":
          s.setObjectXray(!s.objectXray);
          break;
        case "invertSelection":
          s.invertSelection();
          break;
        case "focusSelection":
          s.focusSelection();
          break;

        // ── Object mode ────────────────────────────────────────
        case "joinObjects":
          s.joinObjects();
          break;
        case "mergeObjects":
          s.mergeObjects();
          break;
        case "openObjectDeleteConfirm":
          if (s.selectedObjectTargets.length === 0) break;
          s.setOperatorAnchorPoint({ x: lastPointerX, y: lastPointerY });
          s.setObjectDeleteConfirmOpen(true);
          break;

        // ── Transform entry ────────────────────────────────────
        case "translate":
          // In pose mode, G is disabled — users drag joints directly to translate.
          // Only the gizmo rotation mode is available via keyboard.
          if (hasBoneSelected) break;
          s.enterCommand("translate");
          break;
        case "rotate":
          if (hasBoneSelected) {
            s.setTransformMode("rotate");
            s.enterCommand("boneRotate");
          } else {
            s.enterCommand("rotate");
          }
          break;
        case "scale":
          s.enterCommand("scale");
          break;
        case "relax_bones":
          if (hasBoneSelected) {
            s.setRelaxMenuOpen(true);
          }
          break;
        case "drop":
          if (hasBoneSelected && poseState?.objectId) {
            s.drop(poseState.objectId);
          }
          break;
        case "pin_joint":
          if (hasBoneSelected && poseState?.objectId) {
            s.pushUndo();
            for (const boneName of poseState.selectedBones) {
              s.toggleBonePin(poseState.objectId, boneName);
            }
          }
          break;
        case "refImageMode":
          if (s.workspaceMode === "reference") {
            s.setWorkspaceMode("object");
          } else if (s.referenceLayers.length > 0) {
            s.setWorkspaceMode("reference");
          }
          break;
        case "duplicate":
          s.duplicate();
          break;
        case "toggleFlyMode": {
          const entering = !s.flyMode;
          useStore.setState({ flyMode: entering });
          if (entering) {
            flyMouseDelta.x = 0;
            flyMouseDelta.y = 0;
            document.body.requestPointerLock?.();
          } else {
            cameraKeysRef.current.clear();
            document.exitPointerLock?.();
          }
          break;
        }
        case "deselect":
          if (s.workspaceMode === "render") {
            s.setWorkspaceMode("object");
            break;
          }
          if (hasBoneSelected) s.selectBone(null);
          else if (poseState) s.exitPoseMode();
          else if (s.mode === "edit") s.exitEditMode();
          else s.setMode("navigate");
          break;
        case "view.axis.front":
          toggleAxisView("y", -1);
          break;
        case "view.axis.back":
          toggleAxisView("y", 1);
          break;
        case "view.axis.left":
          toggleAxisView("x", -1);
          break;
        case "view.axis.right":
          toggleAxisView("x", 1);
          break;
        case "view.axis.top":
          toggleAxisView("z", 1);
          break;
        case "view.axis.bottom":
          toggleAxisView("z", -1);
          break;
      }
    }

    // ---------------------------------------------------------------
    // File picker for Ctrl+O
    // ---------------------------------------------------------------
    function openSceneFilePicker() {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = `.pr,.blend,${MESH_IMPORT_ACCEPT}`;
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        const s = useStore.getState();
        const name = file.name.toLowerCase();
        if (name.endsWith(".pr")) {
          if (
            s.sceneDirty &&
            !(await showConfirmDialog(
              "You have unsaved changes. Discard and open this scene?",
            ))
          )
            return;
          await s.loadSceneFromFile(file);
        } else if (name.endsWith(".blend")) {
          await importBlendAndLoad(file, "load");
        } else if (isMeshImportFileName(name)) {
          await importMeshFileAndSpawn(file);
        }
        input.remove();
      };
      document.body.appendChild(input);
      input.click();
    }

    // ---------------------------------------------------------------
    // Determine which keymap contexts are active
    // ---------------------------------------------------------------
    function getActiveContexts(): KeyContext[] {
      const s = useStore.getState();
      const contexts: KeyContext[] = [];
      if (s.mode === "edit" && s.editMode) contexts.push("edit");
      else if (s.sculptMode) contexts.push("sculpt");
      else if (s.sketchMode) contexts.push("sketch");
      else if (s.poseMode) contexts.push("pose");
      else contexts.push("object");
      contexts.push("global");
      return contexts;
    }

    // ---------------------------------------------------------------
    // keydown
    // ---------------------------------------------------------------
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditableField(e)) return;

      const mod = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;
      const alt = e.altKey;
      const key = normalizeKey(e);
      const state = useStore.getState();

      // ── Command buffer mode ──────────────────────────────────
      if (state.commandBuffer != null) {
        // Shift held during a keyboard-initiated drag → precision mode, and
        // when grid snapping is active it also requests the next finer snap step.
        if (e.key === "Shift" && !kbShiftPrecisionRef.current.active) {
          e.preventDefault();
          kbShiftPrecisionRef.current = {
            active: true,
            anchorX: lastPointerX,
            anchorY: lastPointerY,
          };
          if (state.commandBuffer.mouseOrigin) {
            liveDispatch.schedule({
              clientX: lastPointerX,
              clientY: lastPointerY,
            });
            liveDispatch.flush();
          }
          return;
        }

        // GG — edge slide: G while in translate with no mouse movement
        if (
          !mod &&
          key === "g" &&
          state.commandBuffer.type === "translate" &&
          !state.commandBuffer.mouseOrigin
        ) {
          e.preventDefault();
          state.cancelCommand();
          state.enterCommand("edgeSlide");
          return;
        }

        // G/R/S restart: cancel current command and fall through
        if (!mod && (key === "g" || key === "r" || key === "s")) {
          state.cancelCommand();
          // Fall through to normal dispatch below
        } else {
          e.preventDefault();

          // Look up command-context binding
          const action = lookupAction(key, mod, shift, ["command"], alt);
          if (action) {
            dispatch(action);
            return;
          }

          // Value mode switching for parameterized operators (e.g., P → profile during bevel)
          const switchKeys = getCommandMetadata(
            state.commandBuffer.type,
          ).valueModeSwitchKeys;
          if (switchKeys && key in switchKeys) {
            const targetKey = switchKeys[key];
            useStore.setState({
              commandBuffer: {
                ...state.commandBuffer,
                activeValueMode:
                  state.commandBuffer.activeValueMode === targetKey
                    ? null
                    : targetKey,
                mouseOrigin: null, // Reset so mouse delta starts fresh for new param
              },
            });
            return;
          }

          // Numeric input (not in keymap — regex match)
          if (/^[0-9]$/.test(e.key) || e.key === "." || e.key === "-") {
            state.appendCommandDigit(e.key);
          }
          return;
        }
      }

      // ── Zoom keys (hold-to-zoom, tracked in cameraKeysRef) ───
      if (ZOOM_IN_KEYS.has(e.key) || ZOOM_IN_KEYS.has(e.code)) {
        e.preventDefault();
        cameraKeysRef.current.add("zoomIn");
        return;
      }
      if (ZOOM_OUT_KEYS.has(e.key) || ZOOM_OUT_KEYS.has(e.code)) {
        e.preventDefault();
        cameraKeysRef.current.add("zoomOut");
        return;
      }

      // ── Fly mode: WASD/QE move camera without modifiers ───────
      // Checked before keymap so raw WASD is captured during fly.
      // Escape exits fly mode (handled here, not by keymap).
      const inFlyMode = useStore.getState().flyMode;
      if (inFlyMode) {
        if (key === "escape") {
          e.preventDefault();
          useStore.setState({ flyMode: false });
          cameraKeysRef.current.clear();
          document.exitPointerLock?.();
          return;
        }
        // Track shift for speed boost
        if (e.key === "Shift") cameraKeysRef.current.add("flyShift");
        if (!mod && !alt) {
          const camKey = CAMERA_KEYS.get(e.code);
          if (camKey) {
            e.preventDefault();
            cameraKeysRef.current.add(camKey);
            return;
          }
        }
      }

      // ── Keymap lookup ─────────────────────────────────────────
      const contexts = getActiveContexts();
      const action = lookupAction(key, mod, shift, contexts, alt);
      if (action) {
        // Axis-view shortcuts are toggles. Auto-repeat can re-fire the same
        // shortcut while the key is still held, immediately toggling the view
        // back out of orthographic and making the snap look unreliable.
        if (e.repeat && action.startsWith("view.axis.")) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        dispatch(action);
        return;
      }

      // Backtick layout fallback: on non-US layouts the Backquote physical
      // key produces characters other than "`" (e.g. "²" on AZERTY), so
      // the `key: "`"` keymap entry misses. Match on e.code when no
      // modifiers are held so the axis-view pie is reachable everywhere.
      if (e.code === "Backquote" && !mod && !shift && !alt) {
        e.preventDefault();
        dispatch("openViewPie");
        return;
      }
    };

    // ---------------------------------------------------------------
    // keyup — release camera / zoom hold-keys
    // ---------------------------------------------------------------
    const onKeyUp = (e: KeyboardEvent) => {
      const camKey = CAMERA_KEYS.get(e.code);
      if (camKey) cameraKeysRef.current.delete(camKey);

      if (ZOOM_IN_KEYS.has(e.key) || ZOOM_IN_KEYS.has(e.code))
        cameraKeysRef.current.delete("zoomIn");
      if (ZOOM_OUT_KEYS.has(e.key) || ZOOM_OUT_KEYS.has(e.code))
        cameraKeysRef.current.delete("zoomOut");

      if (e.key === "Shift") {
        cameraKeysRef.current.delete("flyShift");
        // Only clear all camera keys on Shift release if NOT in fly mode
        if (!useStore.getState().flyMode) {
          cameraKeysRef.current.clear();
        }
        // Deactivate keyboard-drag precision mode and snap back to coarse position.
        if (kbShiftPrecisionRef.current.active) {
          kbShiftPrecisionRef.current = {
            ...kbShiftPrecisionRef.current,
            active: false,
          };
          const cmdState = useStore.getState();
          if (cmdState.commandBuffer?.mouseOrigin) {
            liveDispatch.schedule({
              clientX: lastPointerX,
              clientY: lastPointerY,
            });
            liveDispatch.flush();
          }
        }
      }
    };

    // ---------------------------------------------------------------
    // blur — clear all held keys
    // ---------------------------------------------------------------
    const onBlur = () => {
      cameraKeysRef.current.clear();
      kbShiftPrecisionRef.current.active = false;
      liveDispatch.cancel();
    };

    // ---------------------------------------------------------------
    // Pointer tracking + command live preview
    // ---------------------------------------------------------------
    let lastPointerX = 0;
    let lastPointerY = 0;
    // RAF-coalesce live-command dispatch. Pointer events can fire faster
    // than the display refresh (200+ Hz on high-rate mice); running
    // applyCommandLive per-event wastes CPU and on dense meshes the work
    // backs up so badly that pointerup arrives before the drag has caught
    // up. We record the latest dispatch intent and flush it once per
    // animation frame. Fly-mode mouse-look (flyMouseDelta) is accumulated
    // eagerly because CameraMovement consumes and zeroes it per frame.
    const liveDispatch = createRafCoalescer<{
      clientX: number;
      clientY: number;
    }>((pending) => {
      if (!pending) return;
      const state = useStore.getState();
      if (!state.commandBuffer || state.commandBuffer.gizmoInitiated) return;
      if (
        kbShiftPrecisionRef.current.active &&
        state.commandBuffer.mouseOrigin
      ) {
        const { anchorX, anchorY } = kbShiftPrecisionRef.current;
        state.applyCommandLive(
          anchorX + (pending.clientX - anchorX) * PRECISION_SCALE,
          anchorY + (pending.clientY - anchorY) * PRECISION_SCALE,
          { snapGridLevelOffset: -1 },
        );
      } else if (kbShiftPrecisionRef.current.active) {
        state.applyCommandLive(pending.clientX, pending.clientY, {
          snapGridLevelOffset: -1,
        });
      } else {
        state.applyCommandLive(pending.clientX, pending.clientY);
      }
    });
    const scheduleDispatch = (clientX: number, clientY: number) => {
      liveDispatch.schedule({ clientX, clientY });
    };

    const onPointerMove = (e: PointerEvent) => {
      lastPointerX = e.clientX;
      lastPointerY = e.clientY;

      // Accumulate raw deltas for fly-mode mouse look. Stays eager because
      // CameraMovement consumes + zeroes flyMouseDelta per frame; throttling
      // here would drop accumulated motion between frames.
      if (useStore.getState().flyMode) {
        flyMouseDelta.x += e.movementX;
        flyMouseDelta.y += e.movementY;
      }

      const state = useStore.getState();
      if (!state.commandBuffer) {
        // Command ended without a keyup — clear precision state.
        kbShiftPrecisionRef.current.active = false;
        liveDispatch.cancel();
        return;
      }

      // Gizmo-initiated commands handle their own precision scaling; skip here.
      if (state.commandBuffer.gizmoInitiated) return;

      scheduleDispatch(e.clientX, e.clientY);
    };

    // ---------------------------------------------------------------
    // Click / right-click to confirm / cancel during command
    // ---------------------------------------------------------------
    const onPointerDown = (e: PointerEvent) => {
      const state = useStore.getState();
      if (!state.commandBuffer) return;
      if (state.commandBuffer.gizmoInitiated) return;

      // Edge loop insert pick-edge mode: let left clicks pass through
      if (
        state.commandBuffer.type === "edgeLoopInsert" &&
        state.commandBuffer.edgeLoopInsertEdge == null
      ) {
        if (e.button === 2) {
          e.preventDefault();
          e.stopPropagation();
          state.cancelCommand();
        }
        return;
      }

      // Edge loop insert with edge picked: touch drag-to-adjust
      if (
        state.commandBuffer.type === "edgeLoopInsert" &&
        state.commandBuffer.edgeLoopInsertEdge &&
        e.pointerType === "touch" &&
        e.button === 0
      ) {
        e.preventDefault();
        e.stopPropagation();
        useStore.setState({
          commandBuffer: {
            ...state.commandBuffer,
            mouseOrigin: null,
          },
        });
        const onUp = () => {
          useStore.getState().confirmCommand();
          window.removeEventListener("pointerup", onUp);
        };
        window.addEventListener("pointerup", onUp, { once: true });
        return;
      }

      // Left click confirms, right click cancels
      if (e.button === 0) {
        e.preventDefault();
        e.stopPropagation();
        state.confirmCommand();
      } else if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        state.cancelCommand();
      }
    };

    // Prevent context menu during command mode
    const onContextMenu = (e: MouseEvent) => {
      if (useStore.getState().commandBuffer) {
        e.preventDefault();
      }
    };

    // Deactivate toggle operators on clicks that miss the edit mesh,
    // EXCEPT extrudePending — a miss IS the intended target for extrude.
    const onPointerUp = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const state = useStore.getState();
      const em = state.editMode;
      if (!em || !state.activeOperator) return;

      if (em.extrudePending) {
        // The tap location IS the extrude target — execute now.
        const armedOperator = state.activeOperator;
        useStore.setState({
          editMode: { ...em, extrudePending: false },
          activeOperator: null,
        });
        if (em.isCurve) {
          state.extendCurve();
        } else if (armedOperator === "extrude_along_normals") {
          state.extrudeAlongNormalsSelection();
        } else if (armedOperator === "extrude_individual") {
          state.extrudeIndividualFacesSelection();
        } else if (armedOperator === "extrude_manifold") {
          state.extrudeManifoldSelection();
        } else {
          state.extrudeSelection();
        }
        return;
      }
    };

    // ---------------------------------------------------------------
    // Scroll wheel — adjust proportional editing radius during command
    // ---------------------------------------------------------------
    const onWheel = (e: WheelEvent) => {
      const state = useStore.getState();

      // ── Fly mode: scroll adjusts focal length ────────────────
      if (state.flyMode) {
        e.preventDefault();
        const dollyScale =
          state.dollySensitivity *
          0.5 *
          (state.precisionLevel > 0 ? state.precisionLevel : 1);
        const absDelta = Math.abs(e.deltaY) * 0.01;
        const factor = 0.85 ** (dollyScale * absDelta);
        const newFl =
          e.deltaY > 0
            ? state.focalLength * factor // scroll down → wider
            : state.focalLength / factor; // scroll up → tighter
        state.setFocalLength(Math.max(10, Math.min(300, Math.round(newFl))));
        return;
      }

      const cb = state.commandBuffer;
      if (!cb) return;

      // Scroll-adjustable int params (e.g., bevel segments, loop cut count)
      const scrollParam = getScrollParam(cb.type);
      if (scrollParam) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -1 : 1;
        const current =
          (cb.params[scrollParam.key] as number) ?? scrollParam.default;
        const newVal = Math.max(
          scrollParam.min,
          Math.min(scrollParam.max, current + delta),
        );
        if (newVal !== current) {
          const newParams = { ...cb.params, [scrollParam.key]: newVal };
          useStore.setState({
            commandBuffer: { ...cb, params: newParams },
          });
          if (
            cb.type === "edgeLoopInsert" &&
            !cb.edgeLoopInsertEdge &&
            cb.edgeLoopHoverEdge
          ) {
            // Pick-edge mode: recompute preview with new cut count
            const s = useStore.getState();
            const em = s.editMode;
            const obj = em && s.sceneObjects.get(em.objectId);
            if (obj) {
              const [hv0, hv1] = cb.edgeLoopHoverEdge;
              const preview = previewEdgeLoop(
                obj.vertices,
                obj.topology,
                hv0,
                hv1,
                newVal,
              );
              const cb2 = s.commandBuffer;
              if (cb2)
                useStore.setState({
                  commandBuffer: { ...cb2, edgeLoopPreview: preview },
                });
            }
          } else if (cb.mouseOrigin || usesTopologyPreview(cb.type)) {
            // Re-apply the operation with updated param
            useStore.getState().applyCommandLive(lastPointerX, lastPointerY);
          }
        }
        return;
      }

      if (!cb.proportionalWeights) return;

      e.preventDefault();

      // Scale radius by scroll amount (finer control with trackpad)
      const delta = e.deltaY > 0 ? 1.1 : 1 / 1.1;
      const newRadius = Math.max(
        1,
        Math.min(500, state.proportionalRadius * delta),
      );
      state.setProportionalRadius(newRadius);

      // Convert screen-space radius → object-local for weight computation
      const em = state.editMode;
      const snap = state.commandPreSnapshot;
      const snapObj =
        em && snap ? commandSnapshotObject(snap, em.objectId) : null;
      if (em && snapObj) {
        const obj = state.sceneObjects.get(em.objectId);
        const worldRef = obj
          ? new THREE.Vector3(obj.position[0], obj.position[1], obj.position[2])
          : new THREE.Vector3();
        const worldR = screenToWorldRadius(newRadius, worldRef);
        const avgScale = obj
          ? (obj.scale[0] + obj.scale[1] + obj.scale[2]) / 3
          : 1;
        const localRadius = avgScale > 1e-8 ? worldR / avgScale : worldR;

        const weights = computeProportionalWeights(
          snapObj.vertices,
          em.selectedVertices,
          localRadius,
          state.proportionalFalloff,
        );
        useStore.setState({
          commandBuffer: { ...cb, proportionalWeights: weights },
        });
        // Re-apply the transform with updated weights
        if (cb.mouseOrigin) {
          useStore.getState().applyCommandLive(lastPointerX, lastPointerY);
        }
      }
    };

    // Capture-phase handler: call preventDefault() early for Cmd/Ctrl
    // shortcuts so macOS browsers don't fire native actions (Cmd+Z undo,
    // Cmd+S save dialog, etc.) before the bubble-phase dispatcher runs.
    const onKeyDownCapture = (e: KeyboardEvent) => {
      if (isEditableField(e)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = normalizeKey(e);
      const shift = e.shiftKey;
      const contexts = getActiveContexts();
      if (lookupAction(key, mod, shift, contexts)) {
        e.preventDefault();
      }
    };

    // Exit fly mode if the browser releases pointer lock (e.g. user pressed
    // Escape before our keydown handler, or the browser revoked it).
    const onPointerLockChange = () => {
      if (!document.pointerLockElement && useStore.getState().flyMode) {
        useStore.setState({ flyMode: false });
        cameraKeysRef.current.clear();
      }
    };

    window.addEventListener("keydown", onKeyDownCapture, true);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("wheel", onWheel, { passive: false });
    document.addEventListener("pointerlockchange", onPointerLockChange);
    return () => {
      window.removeEventListener("keydown", onKeyDownCapture, true);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("wheel", onWheel);
      document.removeEventListener("pointerlockchange", onPointerLockChange);
      liveDispatch.cancel();
    };
  }, []);

  return cameraKeysRef;
}
